import * as React from 'react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { useLocalizedNumberInput } from '@/hooks/useLocalizedNumberInput'

export interface NumberInputProps extends Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange' | 'type'> {
  value: number | null | undefined
  onChange: (value: number | null) => void
  currency?: string
  min?: number
  max?: number
}

function toDraft(value: number | null | undefined): string {
  return value == null ? '' : String(value)
}

function toNumber(draft: string): number | null {
  const num = draft === '' ? NaN : Number(draft)
  return Number.isNaN(num) ? null : num
}

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ className, value, onChange, currency, min, max, step, onKeyDown, ...props }, ref) => {
    // The typed text, so a half-typed "12." keeps its point; reset when the
    // value is changed from outside.
    const [draft, setDraft] = React.useState(() => toDraft(value))
    if (toNumber(draft) !== (value ?? null)) setDraft(toDraft(value))

    const numberProps = useLocalizedNumberInput({
      value: draft,
      onChange: (next) => {
        const num = toNumber(next)
        if (num !== null && min !== undefined && num < min) return
        if (num !== null && max !== undefined && num > max) return
        setDraft(next)
        onChange(num)
      },
      min,
      max,
      step,
      onKeyDown,
    })

    return (
      <div className="relative w-full">
        {currency && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            {currency}
          </span>
        )}
        <Input
          ref={ref}
          {...numberProps}
          className={cn(currency && 'pl-10', className)}
          {...props}
        />
      </div>
    )
  }
)
NumberInput.displayName = 'NumberInput'

export { NumberInput }
